import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Container, Button, Form, Row, Col } from 'react-bootstrap';
import Masonry from 'react-masonry-css';
import { FaWhatsapp } from 'react-icons/fa';
import ecardApi from '../../api/eCards/ecardApi';
import '../styles/e-card.css';
import SpinnerLoader from '../../components/Buttons/SpinnerLoader';
import { useDebounce } from '../../hooks/useDebounce';

const E_card = () => {
  const [items, setItems] = useState([]);
  const [cardType, setCardType] = useState('all');
  const [eventType, setEventType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchTerm = useDebounce(searchInput, 500);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);

  const cardTypeEventMap = {
    'Invitation': ['all', 'Wedding', 'SendOff', 'KitchenParty', 'Engagement', 'Anniversary'],
    'Contribution': ['all', 'Wedding', 'SendOff', 'KitchenParty']
  };

  const fetchECards = useCallback(async (currentPage = 1, search = '', cType = 'all', eType = 'all') => {
    try {
      if (search || cType !== 'all' || eType !== 'all' || currentPage > 1) {
        setSearching(true);
      } else if (currentPage === 1) {
        setLoading(true);
      }

      const params = {
        page: currentPage,
      };
      if (search) params.search = search;
      if (cType !== 'all') params.card_category = cType;
      if (eType !== 'all') params.event_type = eType;

      const response = await ecardApi.searchTemplates(params);

      if (currentPage === 1) {
        setItems(response.templates || []);
      } else {
        setItems(prev => {
          // avoid duplicates just in case
          const newItems = response.templates || [];
          const existingIds = new Set(prev.map(i => i.id));
          return [...prev, ...newItems.filter(i => !existingIds.has(i.id))];
        });
      }
      setPagination(response.pagination);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    fetchECards(1, searchTerm, cardType, eventType);
  }, [searchTerm, cardType, eventType, fetchECards]);

  useEffect(() => {
    if (debouncedSearchTerm !== searchTerm) {
      setSearchTerm(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, searchTerm]);

  const handleCardTypeChange = (type) => {
    setCardType(type);
    setEventType('all');
  };

  const handleEventTypeChange = (type) => {
    setEventType(type);
  };

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
  };




  const loadMore = () => {
    if (pagination && page < pagination.total_pages) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchECards(nextPage, searchTerm, cardType, eventType);
    }
  };

  const availableEventTypes = cardType === 'all' ? [] : (cardTypeEventMap[cardType] || []);

  const breakpointColumnsObj = {
    default: 4,
    1100: 3,
    700: 2,
    500: 2
  };

  return (
    <Container>
      <br />   <br />  <br />
      <h2 className="text-center mb-4 stylish-heading">Digital Card</h2>

      <Row className="mb-4">
        <Col xs={12} className="mb-4">
          <div className="ecard-filters-wrapper">
            <div className="ecard-segmented-control mb-4">
              <div
                className={`segment-btn ${cardType === 'all' ? 'active' : ''}`}
                onClick={() => handleCardTypeChange('all')}
              >
                All Types
              </div>
              <div
                className={`segment-btn ${cardType === 'Invitation' ? 'active' : ''}`}
                onClick={() => handleCardTypeChange('Invitation')}
              >
                Invitation
              </div>
              <div
                className={`segment-btn ${cardType === 'Contribution' ? 'active' : ''}`}
                onClick={() => handleCardTypeChange('Contribution')}
              >
                Contribution
              </div>
            </div>

            {cardType !== 'all' && availableEventTypes.length > 0 && (
              <div className="ecard-event-filters mt-4 pt-3" style={{ borderTop: '1px dashed #e9ecef' }}>
                <h6 className="event-filter-title text-center mb-3 text-muted">Select Event Type</h6>
                <div className="d-flex flex-wrap justify-content-center gap-2">
                  {availableEventTypes.map(event => (
                    <div
                      key={event}
                      className={`event-pill-btn ${eventType === event ? 'active' : ''}`}
                      onClick={() => handleEventTypeChange(event)}
                    >
                      {event === 'all' ? 'All Events' : event}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Col>

        <Col xs={12} md={12}>
          <Form className="search-form" onSubmit={(e) => e.preventDefault()}>
            <div className="search-input-container position-relative">
              <Form.Control
                type="text"
                placeholder="Search by Name, Color or Event type"
                value={searchInput}
                onChange={handleSearchChange}
                className="search-input"
                size="sm"
                style={{ paddingRight: '120px' }}
              />
              {searching && (
                <div style={{ position: 'absolute', right: '100px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: '0.85rem', zIndex: 10 }}>
                  Searching...
                </div>
              )}
              <Link to="/gharama" className="gharama-link" style={{ zIndex: 11 }}>
                <Button variant="outline-primary" className="gharama-btn" size="sm">
                  Gharama
                </Button>
              </Link>
            </div>
          </Form>
        </Col>
      </Row>

      {loading && page === 1 ? (
        <SpinnerLoader />
      ) : items.length === 0 ? (
        <div className="text-center py-5">
          <h4>No cards found</h4>
          <p className="text-muted">Try adjusting your filters or search terms.</p>
        </div>
      ) : (
        <>
          <Masonry
            breakpointCols={breakpointColumnsObj}
            className="my-masonry-grid"
            columnClassName="my-masonry-grid_column">
            {items.map(item => {
              const priceInt = parseInt(item.price) || 0;
              const imgUrl = item.photo_path || item.lg_path || item.md_path || item.wallpaper;

              return (
                <div key={item.id} className="card-wrapper position-relative">
                  <div className="price-tab d-flex align-items-center gap-2">
                    <span>TZS {priceInt.toLocaleString()}</span>
                    {priceInt >= 750 && (
                      <FaWhatsapp className="text-white" size={16} title="WhatsApp Support Available" />
                    )}
                  </div>
                  <Link to={`/card-details/${item.id}`} state={{ card: item }}>
                    <img src={imgUrl} alt={item.name} className="card-img" />
                  </Link>
                </div>
              );
            })}
          </Masonry>

          {pagination && page < pagination.total_pages && (
            <div className="text-center mt-4 mb-5">
              <Button
                variant="primary"
                onClick={loadMore}
                disabled={searching || loading}
                style={{ padding: '10px 30px', borderRadius: '30px' }}
              >
                {(searching && page > 1) ? 'Loading...' : 'Load More'}
              </Button>
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default E_card;
